import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAppState } from "../app/slice/appSlice";
import { selectConditionsState } from "../app/slice/conditionSlice";
import { selectUnsavedState } from "../app/slice/unsavedSlice";
import {
  selectAiChatContext,
  setAiChatConditionId,
  setAiChatHasUnsavedChanges,
  setAiChatPage,
  setAiChatSlotId,
  setAiChatSlotStatus,
  setAiChatVariantId,
} from "../app/slice/aiChatContextSlice";
import { APP_PLATFORM } from "../config";
import { Platform } from "../models";
import {
  AIChatbotPostMessageEventType,
  IFramePostMessageType,
  sendPostMessage,
} from "../utils/helpers";

/**
 * Single owner of all `UPDATE_AI_CHAT_CONTEXT` postMessage emissions.
 *
 * Phase 1 (producers): subscribe to the canonical Redux state that already
 * lives elsewhere (app, conditions, unsaved) and feed the `aiChatContext`
 * slice via dedicated actions.
 *
 * Phase 2 (senders): subscribe to the `aiChatContext` slice; one targeted
 * effect per field emits `sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, ...)`
 * with the matching `AIChatbotPostMessageEventType` whenever that field changes.
 *
 * Mounted once at the top level (alongside `useAIChatContext`) so it lives
 * for the entire app lifetime.
 */
const useAIChatContextSync = () => {
  const dispatch = useAppDispatch();
  const isShopify = APP_PLATFORM === Platform.Shopify;

  const { activePage, showFlowBuilder, isMobile } = useAppSelector(selectAppState);
  const { conditions, selectedConditionId, selectedVariantId, selectedSlot } =
    useAppSelector(selectConditionsState);
  const { unsavedChanges } = useAppSelector(selectUnsavedState);

  const selectedVariantStatus = useMemo(() => {
    if (!selectedVariantId) return undefined;

    const selectedCondition = conditions.find((c) => c.id === selectedConditionId);
    const variant = selectedCondition?.variants?.find((v: any) => v.id === selectedVariantId);

    if (!variant) return undefined;

    return variant.status ? "ENABLED" : "DISABLED";
  }, [conditions, selectedConditionId, selectedVariantId]);

  const hasUnsavedChanges = unsavedChanges.length > 0;

  // --- Phase 1: producers — feed the slice from canonical Redux state ---

  useEffect(() => {
    if (!isShopify) return;
    if(isMobile){
      dispatch(setAiChatPage(activePage as any));
      return;
    }
    dispatch(setAiChatPage(showFlowBuilder ? (activePage as any) : undefined));
  }, [dispatch, isShopify, showFlowBuilder, activePage, isMobile]);

  useEffect(() => {
    if (!isShopify) return;
    dispatch(setAiChatConditionId(selectedConditionId || ""));
  }, [dispatch, isShopify, selectedConditionId]);

  useEffect(() => {
    if (!isShopify) return;
    dispatch(setAiChatVariantId(selectedVariantId || undefined));
  }, [dispatch, isShopify, selectedVariantId]);

  useEffect(() => {
    if (!isShopify) return;
    dispatch(setAiChatSlotId(selectedSlot?.id || undefined));
  }, [dispatch, isShopify, selectedSlot?.id]);

  useEffect(() => {
    if (!isShopify) return;
    dispatch(setAiChatSlotStatus(selectedVariantStatus));
  }, [dispatch, isShopify, selectedVariantStatus]);

  useEffect(() => {
    if (!isShopify) return;
    dispatch(setAiChatHasUnsavedChanges(hasUnsavedChanges));
  }, [dispatch, isShopify, hasUnsavedChanges]);

  // --- Phase 2: senders — one effect per field, emits postMessage on change ---

  const ctx = useAppSelector(selectAiChatContext);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      currentView: ctx.currentView,
      eventType: AIChatbotPostMessageEventType.UPDATE_CURRENT_VIEW,
    });
  }, [isShopify, ctx.currentView]);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      page: ctx.page,
      eventType: AIChatbotPostMessageEventType.SET_SELECTED_PAGE,
    });
  }, [isShopify, ctx.page]);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      conditionId: ctx.conditionId,
      eventType: AIChatbotPostMessageEventType.SET_SELECTED_CONDITION,
    });
  }, [isShopify, ctx.conditionId]);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      variantId: ctx.variantId,
      eventType: AIChatbotPostMessageEventType.SET_SELECTED_VARIANT,
    });
  }, [isShopify, ctx.variantId]);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      slotId: ctx.slotId,
      eventType: AIChatbotPostMessageEventType.SET_SELECTED_SLOT,
    });
  }, [isShopify, ctx.slotId]);

  useEffect(() => {
    if (!isShopify) return;
    if (!ctx.slotStatus) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      slotStatus: ctx.slotStatus,
      eventType: AIChatbotPostMessageEventType.SET_SELECTED_SLOT_STATUS,
    });
  }, [isShopify, ctx.slotStatus]);

  useEffect(() => {
    if (!isShopify) return;
    sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
      hasUnsavedChanges: ctx.hasUnsavedChanges,
      eventType: AIChatbotPostMessageEventType.UPDATE_HAS_UNSAVED_CHANGES,
    });
  }, [isShopify, ctx.hasUnsavedChanges]);
};

export default useAIChatContextSync;
